/**
 * lib/StateMaterial.tsx — verification state rendered as a MATERIAL.
 *
 * ── WHY THIS EXISTS ───────────────────────────────────────────────────────
 * State in this product is always a badge and never a material: an approved
 * org and a pending org render identically apart from a coloured pill you have
 * to go and find. Meanwhile the codebase already ships both materials for
 * exactly this and uses neither for it:
 *
 *   `.cert-seal`            — the struck gold-foil medallion from the stone
 *                             grading report. Says APPROVED / stamped.
 *   `.order-node--upcoming` — a hollow hairline ring on paper. Says PENDING /
 *                             not yet reached.
 *
 * This module exposes that pair as one vocabulary covering the four states the
 * org surfaces actually have — approved, pending, rejected, suspended — so
 * verification reads across the room instead of hiding in a chip.
 *
 * ── HONESTY RULES ─────────────────────────────────────────────────────────
 * 1. The struck gold seal means APPROVED and nothing else. A document that was
 *    merely RECEIVED gets the hollow ring. Striking gold on receipt tells a
 *    supplier they are verified when a human has not looked yet.
 * 2. Rejected is not a red seal — it is a cool, unstruck disc with a slash.
 *    A rejection is recoverable; it must not look like a brand.
 * 3. Suspended (a paused seat after a failed payment) is the approved
 *    medallion DESATURATED, because that is literally what it is: verification
 *    you still hold, switched off. The user reads "paused", not "revoked".
 *
 * ── ACCESSIBILITY ─────────────────────────────────────────────────────────
 * Colour and material alone never carry the meaning. `<StateSeal>` renders a
 * distinct GLYPH per state (check / hairline ring / slash / pause) and a
 * `role="img"` + `aria-label`, so it survives greyscale, colour-blindness and
 * a screen reader. `<StateMark>` adds the word.
 *
 * Reduced motion: the strike animation lives in `index.css` next to its
 * `@keyframes` with its own `prefers-reduced-motion` block; `animate` is also
 * opt-in and off by default.
 */

export type VerificationState = 'approved' | 'pending' | 'rejected' | 'suspended'

/** The word a human reads. Kept here so every surface says the same thing. */
export function stateLabel(state: VerificationState): string {
  switch (state) {
    case 'approved': return 'Verified'
    case 'pending': return 'Verification pending'
    case 'rejected': return 'Not verified'
    case 'suspended': return 'Paused'
  }
}

/**
 * Map the server's `kycStatus` (`not_submitted | submitted | approved |
 * rejected`, on both `users/{uid}` and `orgs/{orgId}`) onto the material.
 * `not_submitted` and `submitted` are both "pending" to a viewer — the
 * difference is whose move it is, which belongs in the copy next to the seal,
 * not in the seal.
 */
export function stateFromKyc(
  kycStatus: 'not_submitted' | 'submitted' | 'approved' | 'rejected' | null | undefined,
  opts: { suspended?: boolean } = {},
): VerificationState {
  // A suspended seat outranks the org's own verification: the org may well be
  // approved while THIS person's seat is switched off, and the seat is what
  // decides whether they see a price.
  if (opts.suspended) return 'suspended'
  if (kycStatus === 'approved') return 'approved'
  if (kycStatus === 'rejected') return 'rejected'
  return 'pending'
}

const SEAL_CLASS: Record<VerificationState, string> = {
  approved: 'cert-seal state-seal--approved',
  pending: 'state-seal--pending',
  rejected: 'state-seal--rejected',
  suspended: 'cert-seal state-seal--suspended',
}

function Glyph({ state, size }: { state: VerificationState; size: number }) {
  // Stroke scales with the disc so a 20px seal and a 96px crest read the same.
  const s = Math.max(1.2, size * 0.075)
  const common = {
    width: size, height: size, viewBox: '0 0 24 24', fill: 'none',
    strokeWidth: s, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
  }
  switch (state) {
    case 'approved':
      // Struck into the foil — navy ink, as on the grading-report seal.
      return <svg {...common} stroke="rgba(10,31,60,0.78)"><path d="M7 12.4l3.2 3.2L17 8.8" /></svg>
    case 'pending':
      // Deliberately NOT a clock: an arc that hasn't closed yet.
      return <svg {...common} stroke="rgba(14,42,79,0.42)"><path d="M12 6.5a5.5 5.5 0 1 1-4.9 3" /></svg>
    case 'rejected':
      return <svg {...common} stroke="rgba(163,58,58,0.85)"><path d="M8.4 8.4l7.2 7.2M15.6 8.4l-7.2 7.2" /></svg>
    case 'suspended':
      return <svg {...common} stroke="rgba(10,31,60,0.55)"><path d="M10 8.5v7M14 8.5v7" /></svg>
  }
}

/**
 * The medallion on its own. Use next to a name (org header, member row, doc
 * slot, KYC status). `size` is the disc diameter in px.
 */
export function StateSeal({
  state,
  size = 22,
  animate = false,
  className = '',
  testId,
}: {
  state: VerificationState
  size?: number
  /** Play the strike (approved) / settle (others) once on mount. */
  animate?: boolean
  className?: string
  testId?: string
}) {
  return (
    <span
      role="img"
      aria-label={stateLabel(state)}
      data-state={state}
      data-testid={testId}
      className={`state-seal ${SEAL_CLASS[state]} ${animate ? 'state-seal--strike' : ''} ${className}`.trim()}
      style={{ width: size, height: size }}
    >
      <Glyph state={state} size={Math.round(size * 0.62)} />
    </span>
  )
}

/**
 * Seal + word, for a row or a header line. The label is the accessible name,
 * so the seal inside is hidden from AT to avoid reading it twice.
 */
export function StateMark({
  state,
  label,
  size = 20,
  animate = false,
  className = '',
  testId,
}: {
  state: VerificationState
  /** Override the default wording (e.g. "Seat paused"). */
  label?: string
  size?: number
  animate?: boolean
  className?: string
  testId?: string
}) {
  const text = label ?? stateLabel(state)
  return (
    <span className={`state-mark ${className}`.trim()} data-state={state} data-testid={testId}>
      <span aria-hidden="true" className="contents">
        <StateSeal state={state} size={size} animate={animate} />
      </span>
      <span className="state-mark__label">{text}</span>
    </span>
  )
}
